import React from "react";
import { withRouter } from "react-router";
import { Link } from "react-router";
import { withUser } from "@unifiedpatents/sso";
import Helmet from "react-helmet";
import TableWithControls from "../common/table-with-controls";
import { withTopCompanies } from "../common/top-companies";
import { getCases, esqCases, ES_URL } from "../../services/api-maxval";
import { keepOnlyLitigationQueryStrings } from "../../helpers/url";
import { toDateString, toSetString, toPatentDetailLink } from "../../helpers/converters";
import { isArray, startCase } from "lodash";

const SearchResult = ({ router, location, user, companiesRenderer }) => {
  const query = keepOnlyLitigationQueryStrings(location.query);
  const descriptionExtra = Object.keys(query).length === 1 && query.patents ? ` for Patent ${query.patents}` : "";
  const helmet = {
    title: `Litigation Case List${descriptionExtra}`,
    meta: [
      {
        name: "keywords",
        content: "Litigation Search,litigation cases,litigation database,PACER,District Court,Supreme Court,United States Court of Federal Claims,Court of Appeals for the Federal Circuit,International Trade Commission"
      },
      {
        name: "description",
        content: `Litigation Cases${descriptionExtra}. Search patent litigations in District Courts, Supreme Court, United States Court of Federal Claims, CAFC and ITC.`
      }
    ]
  };
  const [queries, setQueries] = React.useState(query); // include transient pagination states
  const fetcher = q => getCases(q).then(json => ({
    data: json.hits.hits.map(e => ({ ...e._source, highlight: e.highlight })),
    total: json.hits.total.value,
  }))
  const onChange = q => {
    setQueries(q);
    router.push({
      pathname: router.location.pathname,
      query: keepOnlyLitigationQueryStrings(q)
    });
  }
  const COLUMNS = [
    {
      title: "Source",
      dataIndex: "flag",
      key: "flag",
      width: 100
    },
    {
      title: "Case",
      dataIndex: "case_no",
      key: "case_no",
      sorter: true,
      width: 160,
      render: (caseNum, record) => <Link to={`/litigation/${record.court}/case/${caseNum}`}>{caseNum}</Link>
    },
    {
      title: "Filing Date",
      dataIndex: "filed_date",
      key: "filed_date",
      sorter: true,
      width: 140,
      render: toDateString
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: startCase,
      sorter: true,
      width: 100
    },
    {
      title: "Court",
      dataIndex: "court",
      key: "court",
      width: 300
    },
    {
      title: "Plaintiff",
      dataIndex: "plaintiff",
      key: "plaintiff",
      width: 200,
      render: companiesRenderer
    },
    {
      title: "Defendant",
      dataIndex: "defendant",
      key: "defendant",
      width: 200,
      render: companiesRenderer
    },
    {
      title: "Patents in Case",
      dataIndex: "patents",
      key: "patents",
      width: 150,
      render: val => (val && val.length ? val.map((v, i) => <p key={i}>{toPatentDetailLink(v)}</p>) : val)
    },
    {
      title: "Cause of Action",
      dataIndex: "cause_of_action",
      key: "cause_of_action",
      width: 150,
      render: val => (isArray(val) ? toSetString(val) : val)
    },
    {
      title: "Plaintiff Entity Type",
      dataIndex: "entity_type",
      key: "entity_type",
      width: 250
    },
    {
      title: "Third Party Financing",
      dataIndex: "has_tplf",
      key: "has_tplf",
      width: 100,
      render: v => v === true ? "Yes" : v === false ? "No" : v
    },
    {
      title: "Plaintiff NPE Aggregator",
      dataIndex: "npe_aggregator",
      key: "npe_aggregator",
      width: 250,
      render: toSetString
    },
    {
      title: "Defendant Entity Type",
      dataIndex: "sme_annotation",
      key: "sme_annotation",
      width: 250
    },
    {
      title: "Industry",
      dataIndex: "industry",
      key: "industry",
      width: 100,
      render: v => v ? {
        T: "High-Tech",
        M: "Medical",
        O: "Other"
      }[v] : v
    },
    {
      title: "Judge",
      dataIndex: "judge",
      key: "judge",
      width: 200,
      render: toSetString
    },
    {
      title: "Termination Date",
      dataIndex: "closed_date",
      key: "closed_date",
      width: 140,
      render: toDateString
    },
    {
      title: "Infringed Product",
      dataIndex: "product",
      key: "product",
      width: 300
    }
  ];
  return (
    <>
      <Helmet {...helmet} />
      <TableWithControls
        queries={queries}
        fetcher={fetcher}
        onChange={onChange}
        columns={COLUMNS}
        columnKeysSelected={["case_no", "filed_date", "status", "court", "plaintiff", "defendant", "cause_of_action", "entity_type", "industry"]}
        rowKey="case_no"
        tableKey="litigationTable"
        shouldExport={user.status.canAccessData && user.metadata.authorization.roles.indexOf("disallow-case-export") < 0}
        exportESUrl={ES_URL}
        exportESQueryBuilder={esqCases}
      />
    </>
  );
};

export default withUser(withRouter(withTopCompanies(SearchResult)));
